// src/pages/admin/OrderManagement.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, productsAPI } from '../../services/api';
import { 
  Filter, 
  Loader, 
  AlertCircle, 
  Search, 
  Eye, 
  Coins,
  ShoppingBag,
  Package,
  Truck,
  CheckCircle,
  Clock,
  X,
  Tag,
  Settings,
  ChevronDown,
  Calendar,
  User,
  Mail
} from 'lucide-react';
import './css/OrderManagement.css';

// Safe option label formatter
const formatOptionLabel = (group, value) => {
  const extraPrice = Number(value.extra_price) || 0;
  const extra = extraPrice > 0 ? ` (+DT ${extraPrice.toFixed(2)})` : '';
  return `${group.name}: ${value.value}${extra}`;
};

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [enrichedOrders, setEnrichedOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await ordersAPI.getAll();
        setOrders(data);

        // Enrich orders with option prices
        const enriched = await enrichOrdersWithOptions(data);
        setEnrichedOrders(enriched);
        setFilteredOrders(enriched);
        setLoading(false);
      } catch (err) {
        setError('Impossible de charger les commandes');
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const enrichOrdersWithOptions = async (ordersList) => {
    const itemsWithOptions = ordersList.flatMap(order => 
      order.items
        .filter(item => {
          let parsedOptions = {};
          try {
            parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
          } catch {
            parsedOptions = {};
          }
          return parsedOptions && Object.keys(parsedOptions).length > 0;
        })
        .map(item => ({ ...item, orderId: order.id }))
    );

    if (itemsWithOptions.length === 0) {
      return ordersList.map(order => ({
        ...order,
        enrichedItems: order.items.map(item => ({
          ...item,
          finalPrice: Number(item.price || 0),
          optionLabels: null
        }))
      }));
    }

    const uniqueProductIds = [...new Set(itemsWithOptions.map(item => item.productId))];
    const productResponses = await Promise.allSettled(
      uniqueProductIds.map(id => productsAPI.getById(id))
    );

    const productsMap = {};
    productResponses.forEach((resp, idx) => {
      if (resp.status === 'fulfilled') {
        productsMap[uniqueProductIds[idx]] = resp.value.data;
      }
    });

    return ordersList.map(order => {
      const enrichedItems = order.items.map(item => {
        let parsedOptions = {};
        try {
          parsedOptions = typeof item.selected_options === 'string' ? JSON.parse(item.selected_options) : item.selected_options;
        } catch {
          parsedOptions = {};
        }

        const fullProduct = productsMap[item.productId];
        if (!fullProduct || Object.keys(parsedOptions).length === 0) {
          return {
            ...item,
            finalPrice: Number(item.price || 0),
            optionLabels: null
          };
        }

        const basePrice = Number(fullProduct.salePrice || fullProduct.regularPrice || 0);
        let optionExtra = 0;
        const labels = [];

        fullProduct.options.forEach(group => {
          const selectedValueId = parsedOptions[group.id];
          if (selectedValueId) {
            const value = group.values.find(v => v.id === Number(selectedValueId));
            if (value) {
              const extra = Number(value.extra_price) || 0;
              optionExtra += extra;
              labels.push(formatOptionLabel(group, value));
            }
          }
        });

        const finalPrice = basePrice + optionExtra;

        return {
          ...item,
          finalPrice,
          optionLabels: labels.length > 0 ? labels : null,
          name: fullProduct.name || item.name
        };
      });

      return {
        ...order,
        enrichedItems
      };
    });
  };

  useEffect(() => {
    let filtered = enrichedOrders;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    if (searchQuery) {
      filtered = filtered.filter(order =>
        order.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${order.firstName} ${order.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.id.toString().includes(searchQuery)
      );
    }
    setFilteredOrders(filtered);
  }, [statusFilter, searchQuery, enrichedOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      setEnrichedOrders(prev =>
        prev.map(order =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      setSuccess('Statut de la commande mis a jour !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Echec de mise a jour du statut de la commande');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return <Clock size={16} />;
      case 'processing': return <Loader size={16} />;
      case 'shipped': return <Truck size={16} />;
      case 'delivered': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'omg-pending';
      case 'processing': return 'omg-processing';
      case 'shipped': return 'omg-shipped';
      case 'delivered': return 'omg-delivered';
      default: return 'omg-default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'En attente';
      case 'processing': return 'En cours';
      case 'shipped': return 'Expedie';
      case 'delivered': return 'Livree';
      default: return status;
    }
  };

  const getImageUrl = (imageUrl) => {
    return imageUrl && !imageUrl.startsWith('http') ? `/images/${imageUrl}` : imageUrl || '/placeholder-image.jpg';
  };

  // Calculate stats
  const stats = {
    total: enrichedOrders.length,
    pending: enrichedOrders.filter(o => o.status === 'pending').length,
    processing: enrichedOrders.filter(o => o.status === 'processing').length,
    shipped: enrichedOrders.filter(o => o.status === 'shipped').length,
    delivered: enrichedOrders.filter(o => o.status === 'delivered').length,
  };

  if (loading) {
    return (
      <div className="omg-loading-container">
        <div className="omg-loading-spinner-wrapper">
          <Loader className="omg-loading-spinner" size={48} />
        </div>
        <p className="omg-loading-text">Chargement des commandes...</p>
      </div>
    );
  }

  if (error && enrichedOrders.length === 0) {
    return (
      <div className="omg-error-container">
        <div className="omg-error-icon-wrapper">
          <AlertCircle size={48} className="omg-error-icon" />
        </div>
        <h2 className="omg-error-title">Impossible de charger les commandes</h2>
        <p className="omg-error-text">{error}</p>
        <button onClick={() => window.location.reload()} className="omg-retry-button">
          Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="omg-container">
      {/* Header */}
      <div className="omg-header">
        <div className="omg-header-content">
          <div className="omg-header-left">
            <div className="omg-header-icon-wrapper">
              <ShoppingBag size={28} className="omg-header-icon" />
            </div>
            <div className="omg-header-text">
              <h1 className="omg-title">Gestion des commandes</h1>
              <p className="omg-subtitle">Gerer et suivre toutes les commandes clients</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(success || error) && (
        <div className="omg-alerts">
          {success && (
            <div className="omg-alert omg-alert-success">
              <CheckCircle size={20} className="omg-alert-icon" />
              <span>{success}</span>
              <button className="omg-alert-close" onClick={() => setSuccess('')}>
                <X size={16} />
              </button>
            </div>
          )}
          {error && (
            <div className="omg-alert omg-alert-error">
              <AlertCircle size={20} className="omg-alert-icon" />
              <span>{error}</span>
              <button className="omg-alert-close" onClick={() => setError('')}>
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="omg-stats-grid">
        <div className="omg-stat-card">
          <div className="omg-stat-icon omg-stat-total">
            <ShoppingBag size={24} />
          </div>
          <div className="omg-stat-content">
            <p className="omg-stat-label">Total commandes</p>
            <h3 className="omg-stat-value">{stats.total}</h3>
          </div>
        </div>
        <div className="omg-stat-card">
          <div className="omg-stat-icon omg-stat-pending">
            <Clock size={24} />
          </div>
          <div className="omg-stat-content">
            <p className="omg-stat-label">En attente</p>
            <h3 className="omg-stat-value">{stats.pending}</h3>
          </div>
        </div>
        <div className="omg-stat-card">
          <div className="omg-stat-icon omg-stat-processing">
            <Package size={24} />
          </div>
          <div className="omg-stat-content">
            <p className="omg-stat-label">En cours</p>
            <h3 className="omg-stat-value">{stats.processing}</h3>
          </div>
        </div>
        <div className="omg-stat-card">
          <div className="omg-stat-icon omg-stat-shipped">
            <Truck size={24} />
          </div>
          <div className="omg-stat-content">
            <p className="omg-stat-label">Expediees</p>
            <h3 className="omg-stat-value">{stats.shipped}</h3>
          </div>
        </div>
        <div className="omg-stat-card">
          <div className="omg-stat-icon omg-stat-delivered">
            <CheckCircle size={24} />
          </div>
          <div className="omg-stat-content">
            <p className="omg-stat-label">Livrees</p>
            <h3 className="omg-stat-value">{stats.delivered}</h3>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="omg-filters-bar">
        <div className="omg-search-wrapper">
          <Search size={18} className="omg-search-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par nom client, email ou ID de commande..."
            className="omg-search-input"
          />
        </div>
        <div className="omg-filter-wrapper">
          <Filter size={18} className="omg-filter-icon" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="omg-filter-select"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="processing">En cours</option>
            <option value="shipped">Expedie</option>
            <option value="delivered">Livree</option>
          </select>
        </div>
      </div>

      {/* Orders Card */}
      <div className="omg-card">
        <div>
          <div className="omg-card-header">
            <h2 className="omg-card-title">
              Commandes
              <span className="omg-count-badge">{filteredOrders.length}</span>
            </h2>
          </div>
          
          {filteredOrders.length === 0 ? (
            <div className="omg-empty-state">
              <div className="omg-empty-icon-wrapper">
                <Package size={64} className="omg-empty-icon" />
              </div>
              <h3 className="omg-empty-title">Aucune commande trouvee</h3>
              <p className="omg-empty-text">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Essayez d ajuster votre recherche ou vos filtres.' 
                  : 'Les commandes apparaitront ici apres les achats des clients.'}
              </p>
            </div>
          ) : (
            <div className="omg-table-wrapper">
              <table className="omg-table">
                <thead>
                  <tr>
                    <th className="omg-th">Details commande</th>
                    <th className="omg-th">Client</th>
                    <th className="omg-th">Articles</th>
                    <th className="omg-th">Montant</th>
                    <th className="omg-th">Statut</th>
                    <th className="omg-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const subtotal = order.enrichedItems.reduce((sum, item) => sum + (item.finalPrice * item.quantity), 0);
                    const discount = Number(order.discount_amount || 0);
                    const deliveryFee = Number(order.delivery_fee || 0);
                    const total = Math.max(0, subtotal - discount + deliveryFee);

                    return (
                      <tr key={order.id} className="omg-tr">
                        <td className="omg-td">
                          <div className="omg-order-info">
                            <span className="omg-order-id">#{order.id}</span>
                            <div className="omg-order-date">
                              <Calendar size={12} />
                              <span>{new Date(order.createdAt).toLocaleDateString('fr-FR', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}</span>
                            </div>
                          </div>
                        </td>
                        <td className="omg-td">
                          <div className="omg-customer-info">
                            <div className="omg-customer-name">
                              <User size={14} />
                              <span>{order.firstName} {order.lastName}</span>
                            </div>
                            <div className="omg-customer-email">
                              <Mail size={12} />
                              <span>{order.email || 'Invite'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="omg-td">
                          <div className="omg-items-preview">
                            {order.enrichedItems.slice(0, 3).map((item, idx) => (
                              <div key={idx} className="omg-item-row">
                                <img 
                                  src={getImageUrl(item.imageUrl)} 
                                  alt={item.name || 'Produit'}
                                  className="omg-item-image"
                                  onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                                />
                                <div className="omg-item-details">
                                  <span className="omg-item-name">
                                    {item.name}
                                  </span>
                                  <div className="omg-item-meta">
                                    <span className="omg-item-qty">Qte : {item.quantity}</span>
                                    {item.optionLabels && (
                                      <div className="omg-item-options">
                                        <Settings size={10} />
                                        <span>{item.optionLabels[0]}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {order.enrichedItems.length > 3 && (
                              <span className="omg-more-items">
                                +{order.enrichedItems.length - 3} article{order.enrichedItems.length - 3 > 1 ? 's' : ''} en plus
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="omg-td">
                          <div className="omg-pricing-info">
                            <div className="omg-total-amount">
                              <Coins size={18} />
                              <span>DT {total.toFixed(2)}</span>
                            </div>
                            <div className="omg-pricing-details">
                              {discount > 0 && (
                                <span className="omg-discount">
                                  <Tag size={11} />
                                  <span>-DT {discount.toFixed(2)}</span>
                                </span>
                              )}
                              <span className="omg-delivery">
                                <Truck size={11} />
                                <span>{deliveryFee === 0 ? 'Gratuit' : `DT ${deliveryFee.toFixed(2)}`}</span>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="omg-td">
                          <div className="omg-status-wrapper">
                            <select
                              value={order.status}
                              onChange={(e) => handleStatusChange(order.id, e.target.value)}
                              className={`omg-status-select ${getStatusColor(order.status)}`}
                            >
                              <option value="pending">En attente</option>
                              <option value="processing">En cours</option>
                              <option value="shipped">Expedie</option>
                              <option value="delivered">Livree</option>
                            </select>
                            <div className={`omg-status-badge ${getStatusColor(order.status)}`}>
                              {getStatusIcon(order.status)}
                              <span>{getStatusLabel(order.status)}</span>
                            </div>
                          </div>
                        </td>
                        <td className="omg-td">
                          <button
                            onClick={() => navigate(`/admin/orders/${order.id}`)}
                            className="omg-view-btn"
                            aria-label="Voir les details de la commande"
                          >
                            <Eye size={16} />
                            <span>Voir details</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;
